/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, MutableRefObject } from 'react';

interface PannellumHotspotConfig {
  id: string;
  pitch: number;
  yaw: number;
  type: string;
  cssClass: string;
  createTooltipFunc: (hotspotDiv: HTMLElement, args: { label: string }) => void;
  createTooltipArgs: { label: string };
  clickHandlerFunc: () => void;
}

interface RenderedHotspot {
  id: number;
  title: string;
}

/**
 * Hook to sync Pannellum viewer hotspots with React Query data.
 * Handles adding, removing, and updating hotspots when their titles change.
 */
export const useSyncPannellumHotspots = (
  pannellumViewerRef: MutableRefObject<any>,
  hotspotsData: { hotspots: Array<{ id: number; pitch: number; yaw: number; title?: string }> } | null | undefined,
  onHotspotClick: (hotspot: any) => void,
  enabled: boolean = true
) => {
  // Track which hotspots are currently rendered in Pannellum (with their titles)
  const renderedHotspotsRef = useRef<Map<number, RenderedHotspot>>(new Map());

  useEffect(() => {
    const viewer = pannellumViewerRef.current;
    
    // Skip if viewer not ready or hotspots disabled
    if (!viewer || !enabled) {
      return;
    }

    // Check if viewer is loaded
    let isLoaded = false;
    try {
      isLoaded = viewer.isLoaded();
    } catch {
      isLoaded = true; // Assume loaded if method doesn't exist
    }

    if (!isLoaded) {
      return;
    }

    const currentHotspots = hotspotsData?.hotspots || [];
    const currentIds = new Set(currentHotspots.map(h => h.id));
    const renderedHotspots = renderedHotspotsRef.current;

    // Find hotspots to add (in data but not rendered)
    const toAdd = currentHotspots.filter(h => !renderedHotspots.has(h.id));
    
    // Find hotspots to remove (rendered but not in data)
    const toRemove = Array.from(renderedHotspots.keys()).filter(id => !currentIds.has(id));

    // Find hotspots that need updating (title changed)
    const toUpdate = currentHotspots.filter(h => {
      const rendered = renderedHotspots.get(h.id);
      if (!rendered) return false;
      const newTitle = h.title || 'Hotspot';
      return rendered.title !== newTitle;
    });

    // Remove stale hotspots
    toRemove.forEach(id => {
      try {
        viewer.removeHotSpot(`hotspot-${id}`);
        renderedHotspots.delete(id);
      } catch {
        renderedHotspots.delete(id);
      }
    });

    // Remove and re-add hotspots with changed titles
    toUpdate.forEach(hotspot => {
      try {
        viewer.removeHotSpot(`hotspot-${hotspot.id}`);
        renderedHotspots.delete(hotspot.id);
      } catch {
        renderedHotspots.delete(hotspot.id);
      }
      // Add to toAdd so it gets re-added below
      toAdd.push(hotspot);
    });

    // Add new hotspots (and updated ones)
    toAdd.forEach(hotspot => {
      const title = hotspot.title || 'Hotspot';
      try {
        const config: PannellumHotspotConfig = {
          id: `hotspot-${hotspot.id}`,
          pitch: hotspot.pitch,
          yaw: hotspot.yaw,
          type: 'info',
          cssClass: 'custom-hotspot',
          createTooltipFunc: (hotspotDiv, args) => {
            hotspotDiv.setAttribute('data-text', args.label);
          },
          createTooltipArgs: {
            label: title,
          },
          clickHandlerFunc: () => {
            onHotspotClick(hotspot);
          },
        };

        viewer.addHotSpot(config);
        renderedHotspots.set(hotspot.id, { id: hotspot.id, title });
      } catch (error) {
        console.error(`Failed to add hotspot ${hotspot.id}:`, error);
      }
    });
    
  }, [pannellumViewerRef, hotspotsData, onHotspotClick, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      renderedHotspotsRef.current.clear();
    };
  }, []);

  return {
    renderedCount: renderedHotspotsRef.current.size,
  };
};

export default useSyncPannellumHotspots;
